import * as React from 'react';

import moment from 'moment';

import {Button} from 'shells/button/button';
import {CalendarWrapper} from 'shells/calendar/calendar-wrapper';
import {Dialog} from 'shells/dialog/dialog';
import {FormField} from 'shells/form/form-field';
import {Message} from 'shells/message/message';
import {Popover} from 'shells/popover/popover';
import {roundTimeToNextQuarter} from 'shells/select-time-picker/helpers';
import {SelectTimePicker} from 'shells/select-time-picker/select-time-picker';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {TwoLine} from 'shells/two-line/two-line';
import {Body} from 'shells/typography/body';
import {ModalBody} from 'shells/typography/modal-body';

import type {CampaignType} from 'nutshell-graphql-types';

import styles from './schedule-view.module.css';

type Props = {
    scheduledDate: moment.Moment;
    setScheduledDate: (arg1: moment.Moment) => void;
    onReviewAndSend?: () => void;
    onUnschedule?: () => Promise<any>;
    type: CampaignType;
};

export function ScheduleView(props: Props) {
    const [isShowingUnscheduleConfirmation, setIsShowingUnscheduleConfirmation] =
        React.useState<boolean>(false);
    const [unscheduleButtonRef, setUnscheduleButtonRef] = React.useState<
        HTMLElement | null | undefined
    >(null);
    const [dateContainerRef, setDateContainerRef] = React.useState<HTMLElement | null | undefined>(
        null
    );
    const [showCalendarPopover, setShowCalendarPopover] = React.useState<boolean>(false);
    const userFacingNoun = props.type === 'NEWSLETTER' ? 'edition' : 'broadcast';

    return (
        <React.Fragment>
            <ModalBody>Select a date and time to send this {userFacingNoun}</ModalBody>
            <div className='flex'>
                <div className={styles['calendar-container']}>
                    <CalendarWrapper
                        momentDisplayDate={props.scheduledDate}
                        onDateClick={(date) => {
                            const time = moment(props.scheduledDate).diff(
                                moment(props.scheduledDate).startOf('day')
                            );
                            const newDate = roundTimeToNextQuarter(moment(date).add(time, 'ms'));

                            if (newDate.isBefore(moment())) {
                                // If the selected date/time is before now, just
                                // set the time to now
                                props.setScheduledDate(roundTimeToNextQuarter(moment()));
                            } else {
                                props.setScheduledDate(
                                    roundTimeToNextQuarter(moment(date).add(time, 'ms'))
                                );
                            }
                        }}
                        minDate={moment()}
                        hasYearPicker={false}
                    />
                </div>
                <div className={styles['right-container']}>
                    <div className={styles['form-container']}>
                        <FormField innerId='date' label='Date'>
                            <div
                                ref={setDateContainerRef}
                                onClick={() => {
                                    setShowCalendarPopover(true);
                                }}
                                className={styles['date-container']}
                            >
                                <FormattedTimestamp
                                    datetime={props.scheduledDate.unix()}
                                    format={FormattedTimestamp.formats.dateWithSlashes}
                                />
                            </div>
                        </FormField>
                        <FormField innerId='time' label={'Time'}>
                            <SelectTimePicker
                                selectedTime={props.scheduledDate}
                                onChange={props.setScheduledDate}
                            />
                        </FormField>
                    </div>
                    <div className={styles['form-container']}>
                        <TwoLine
                            topRow='Sending on'
                            bottomRow={
                                <div className='flex'>
                                    <FormattedTimestamp
                                        datetime={props.scheduledDate.unix()}
                                        format={FormattedTimestamp.formats.longDateWithDay}
                                    />
                                    &nbsp;at&nbsp;
                                    <FormattedTimestamp
                                        datetime={props.scheduledDate.unix()}
                                        format={FormattedTimestamp.formats.time}
                                        addTimezoneAbbreviation={true}
                                    />
                                </div>
                            }
                            isLarge={true}
                        />
                        {props.onUnschedule ? (
                            <Button
                                size='big'
                                variant='text-danger'
                                onClick={() => {
                                    setIsShowingUnscheduleConfirmation(true);
                                }}
                                getButtonRef={setUnscheduleButtonRef}
                            >
                                Unschedule
                            </Button>
                        ) : undefined}
                    </div>
                </div>
            </div>
            {props.onReviewAndSend ? (
                <div>
                    <Message
                        type='info'
                        cta={
                            <Button
                                variant='text-primary'
                                size='big'
                                onClick={props.onReviewAndSend}
                            >
                                Yes, review and send
                            </Button>
                        }
                        hasBorder={true}
                    >
                        <Body>
                            You’re scheduling this {userFacingNoun} for a later date. Want to send
                            immediately?
                        </Body>
                    </Message>
                </div>
            ) : undefined}
            {isShowingUnscheduleConfirmation ? (
                <Dialog
                    anchor={unscheduleButtonRef}
                    question={`This ${userFacingNoun} will no longer be scheduled to send. Are you sure?`}
                    primaryText='Yes, unschedule'
                    onPrimary={() => {
                        if (props.onUnschedule) {
                            props.onUnschedule().then(() => {
                                setIsShowingUnscheduleConfirmation(false);
                            });
                        }
                    }}
                    secondaryText='No, keep scheduling'
                    onSecondary={() => {
                        setIsShowingUnscheduleConfirmation(false);
                    }}
                />
            ) : undefined}
            {showCalendarPopover ? (
                <Popover
                    anchor={dateContainerRef}
                    onBlur={() => {
                        setShowCalendarPopover(false);
                    }}
                    location='bottom'
                    isNubDark={true}
                >
                    <CalendarWrapper
                        momentDisplayDate={props.scheduledDate}
                        onDateClick={(date) => {
                            setShowCalendarPopover(false);
                            props.setScheduledDate(roundTimeToNextQuarter(date));
                        }}
                        minDate={moment()}
                        hasYearPicker={false}
                    />
                </Popover>
            ) : undefined}
        </React.Fragment>
    );
}
